import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import {
  Clock,
  Calendar,
  Globe,
  Settings,
  TestTube,
  CheckCircle,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Save,
  RefreshCw
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const BusinessHoursSetup = () => {
  const [businessHoursConfig, setBusinessHoursConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState('profiles');
  const [editingProfile, setEditingProfile] = useState(null);
  const [newProfile, setNewProfile] = useState({
    name: '',
    timezone: 'America/New_York',
    workingDays: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '10:00', end: '14:00' },
      sunday: { enabled: false, start: '10:00', end: '14:00' }
    }
  });

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  useEffect(() => {
    loadBusinessHoursConfig();
  }, []);

  const loadBusinessHoursConfig = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getBusinessHoursConfiguration();
      if (response && response.success && response.data) {
        setBusinessHoursConfig(response.data);
      } else {
        // Set default empty config to prevent crashes
        setBusinessHoursConfig({
          profiles: [],
          timezones: [],
          defaultProfile: null,
          slaIntegration: { enabled: false },
          testing: { testScenarios: [] },
          analytics: { usageStats: {}, performanceMetrics: {} }
        });
      }
    } catch (error) {
      console.error('Error loading business hours configuration:', error);
      // Set default empty config on error
      setBusinessHoursConfig({
        profiles: [],
        timezones: [],
        defaultProfile: null,
        slaIntegration: { enabled: false },
        testing: { testScenarios: [] },
        analytics: { usageStats: {}, performanceMetrics: {} }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      if (editingProfile) {
        await MockDataService.updateBusinessHoursProfile(editingProfile.id, newProfile);
      } else {
        await MockDataService.createBusinessHoursProfile(newProfile);
      }
      await loadBusinessHoursConfig();
      setEditingProfile(null);
      setNewProfile({
        name: '',
        timezone: 'America/New_York',
        workingDays: {
          monday: { enabled: true, start: '09:00', end: '17:00' },
          tuesday: { enabled: true, start: '09:00', end: '17:00' },
          wednesday: { enabled: true, start: '09:00', end: '17:00' },
          thursday: { enabled: true, start: '09:00', end: '17:00' },
          friday: { enabled: true, start: '09:00', end: '17:00' },
          saturday: { enabled: false, start: '10:00', end: '14:00' },
          sunday: { enabled: false, start: '10:00', end: '14:00' }
        }
      });
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      try {
        await MockDataService.deleteBusinessHoursProfile(profileId);
        await loadBusinessHoursConfig();
      } catch (error) {
        console.error('Error deleting profile:', error);
      }
    }
  };

  const handleTestCalculation = async () => {
    try {
      setTesting(true);
      const testData = {
        profileId: 'default',
        date: '2024-01-22',
        time: '10:00'
      };
      const response = await MockDataService.testBusinessHoursCalculation(testData);
      if (response.success) {
        setTestResult(response.data);
      }
    } catch (error) {
      console.error('Error testing calculation:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleWorkingDayChange = (day, field, value) => {
    setNewProfile(prev => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: {
          ...prev.workingDays[day],
          [field]: value
        }
      }
    }));
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setNewProfile({
      name: profile.name,
      timezone: profile.timezone,
      workingDays: { ...profile.workingDays }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Business Hours Setup</h1>
          <p className="text-gray-600">Configure business hours for SLA calculations and ticket routing</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={loadBusinessHoursConfig} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'profiles', label: 'Profiles', icon: Settings },
            { id: 'default', label: 'Default Settings', icon: Clock },
            { id: 'testing', label: 'Testing', icon: TestTube },
            { id: 'analytics', label: 'Analytics', icon: CheckCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-[#006600] text-[#006600]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Profiles Tab */}
      {activeTab === 'profiles' && (
        <div className="space-y-6">
          {/* Profile List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Business Hours Profiles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(Array.isArray(businessHoursConfig?.profiles) ? businessHoursConfig.profiles : []).map(profile => {
                  if (!profile) return null;
                  return (
                  <div key={profile.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{profile.name}</h3>
                        <p className="text-sm text-gray-600">
                          <Globe className="h-4 w-4 inline mr-1" />
                          {(Array.isArray(businessHoursConfig?.timezones) ? businessHoursConfig.timezones.find(tz => tz && tz.value === profile.timezone)?.label : '') || profile.timezone || 'N/A'}
                        </p>
                        <div className="flex items-center mt-2">
                          <Badge variant={profile.status === 'active' ? 'default' : 'secondary'}>
                            {profile.status}
                          </Badge>
                          <span className="text-sm text-gray-500 ml-2">
                            Updated: {new Date(profile.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleEditProfile(profile)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteProfile(profile.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Profile Form */}
          <Card>
            <CardHeader>
              <CardTitle>
                {editingProfile ? 'Edit Profile' : 'Create New Profile'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="profileName">Profile Name</Label>
                  <Input
                    id="profileName"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter profile name"
                  />
                </div>
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={newProfile.timezone}
                    onValueChange={(value) => setNewProfile(prev => ({ ...prev, timezone: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Array.isArray(businessHoursConfig?.timezones) ? businessHoursConfig.timezones : []).map(timezone => {
                        if (!timezone) return null;
                        return (
                          <SelectItem key={timezone.value} value={timezone.value}>
                            {timezone.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Working Days */}
              <div>
                <Label className="text-base font-semibold">Working Days</Label>
                <div className="mt-4 space-y-3">
                  {daysOfWeek.map(day => (
                    <div key={day.key} className="flex items-center space-x-4 p-3 border rounded-lg">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newProfile.workingDays[day.key].enabled}
                          onChange={(e) => handleWorkingDayChange(day.key, 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <Label className="font-medium">{day.label}</Label>
                      </div>
                      {newProfile.workingDays[day.key].enabled && (
                        <div className="flex items-center space-x-2">
                          <Input
                            type="time"
                            value={newProfile.workingDays[day.key].start}
                            onChange={(e) => handleWorkingDayChange(day.key, 'start', e.target.value)}
                            className="w-32"
                          />
                          <span>to</span>
                          <Input
                            type="time"
                            value={newProfile.workingDays[day.key].end}
                            onChange={(e) => handleWorkingDayChange(day.key, 'end', e.target.value)}
                            className="w-32"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  onClick={() => {
                    setEditingProfile(null);
                    setNewProfile({
                      name: '',
                      timezone: 'America/New_York',
                      workingDays: {
                        monday: { enabled: true, start: '09:00', end: '17:00' },
                        tuesday: { enabled: true, start: '09:00', end: '17:00' },
                        wednesday: { enabled: true, start: '09:00', end: '17:00' },
                        thursday: { enabled: true, start: '09:00', end: '17:00' },
                        friday: { enabled: true, start: '09:00', end: '17:00' },
                        saturday: { enabled: false, start: '10:00', end: '14:00' },
                        sunday: { enabled: false, start: '10:00', end: '14:00' }
                      }
                    });
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={saving || !newProfile.name}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : editingProfile ? 'Update Profile' : 'Create Profile'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Default Settings Tab */}
      {activeTab === 'default' && businessHoursConfig && businessHoursConfig.defaultProfile && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Default Business Hours Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Profile Name</Label>
                    <Input value={businessHoursConfig.defaultProfile.name} disabled />
                  </div>
                  <div>
                    <Label>Timezone</Label>
                    <Input value={(Array.isArray(businessHoursConfig.timezones) ? businessHoursConfig.timezones.find(tz => tz && tz.value === businessHoursConfig.defaultProfile.timezone)?.label : '') || ''} disabled />
                  </div>
                </div>

                <div>
                  <Label className="text-base font-semibold">Working Days</Label>
                  <div className="mt-4 space-y-2">
                    {daysOfWeek.map(day => {
                      const dayConfig = businessHoursConfig.defaultProfile.workingDays[day.key];
                      return (
                        <div key={day.key} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${dayConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className="font-medium">{day.label}</span>
                          </div>
                          {dayConfig.enabled && (
                            <span className="text-sm text-gray-600">
                              {dayConfig.start} - {dayConfig.end}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-base font-semibold">Holidays</Label>
                  <div className="mt-4 space-y-2">
                    {(Array.isArray(businessHoursConfig.defaultProfile.holidays) ? businessHoursConfig.defaultProfile.holidays : []).map(holiday => {
                      if (!holiday) return null;
                      return (
                        <div key={holiday.id || Math.random()} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <span className="font-medium">{holiday.name || 'Unnamed Holiday'}</span>
                            <span className="text-sm text-gray-600 ml-2">
                              {holiday.date ? new Date(holiday.date).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                          <Badge variant="outline">{holiday.type || 'public'}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SLA Integration */}
          <Card>
            <CardHeader>
              <CardTitle>SLA Integration Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessHoursConfig?.slaIntegration?.enabled || false}
                    disabled
                    className="rounded"
                  />
                  <Label>Enable SLA Integration</Label>
                </div>
                <div>
                  <Label>Calculation Method</Label>
                  <Input value={businessHoursConfig?.slaIntegration?.calculationMethod || ''} disabled />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={businessHoursConfig?.slaIntegration?.includeHolidays || false}
                    disabled
                    className="rounded"
                  />
                  <Label>Include Holidays in SLA Calculations</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Testing Tab */}
      {activeTab === 'testing' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TestTube className="h-5 w-5 mr-2" />
                Business Hours Testing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button onClick={handleTestCalculation} disabled={testing}>
                  <TestTube className="h-4 w-4 mr-2" />
                  {testing ? 'Testing...' : 'Test SLA Calculation'}
                </Button>

                {testResult && (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p><strong>Test ID:</strong> {testResult.testId}</p>
                        <p><strong>Is Business Hours:</strong> {testResult.result.isBusinessHours ? 'Yes' : 'No'}</p>
                        <p><strong>Next Business Day:</strong> {new Date(testResult.result.nextBusinessDay).toLocaleString()}</p>
                        <p><strong>SLA Due Date:</strong> {new Date(testResult.result.slaDueDate).toLocaleString()}</p>
                        <p><strong>Working Hours Remaining:</strong> {testResult.result.workingHoursRemaining} hours</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label className="text-base font-semibold">Test Scenarios</Label>
                  <div className="mt-4 space-y-2">
                    {(Array.isArray(businessHoursConfig?.testing?.testScenarios) ? businessHoursConfig.testing.testScenarios : []).map(scenario => {
                      if (!scenario) return null;
                      return (
                        <div key={scenario.id || Math.random()} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold">{scenario.name || 'Unnamed Scenario'}</h4>
                              <p className="text-sm text-gray-600">{scenario.description || 'No description'}</p>
                              <p className="text-sm text-gray-500">
                                Test Date: {scenario.testDate ? new Date(scenario.testDate).toLocaleString() : 'N/A'}
                              </p>
                            </div>
                            <Badge variant="outline">{scenario.expectedResult || 'Unknown'}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && businessHoursConfig && businessHoursConfig.analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Settings className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Profiles</p>
                    <p className="text-2xl font-bold">{businessHoursConfig.analytics?.usageStats?.totalProfiles || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Profiles</p>
                    <p className="text-2xl font-bold">{businessHoursConfig.analytics?.usageStats?.activeProfiles || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Holidays</p>
                    <p className="text-2xl font-bold">{businessHoursConfig.analytics?.usageStats?.totalHolidays || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Working Hours</p>
                    <p className="text-2xl font-bold">{businessHoursConfig.analytics?.usageStats?.averageWorkingHours || 0}h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {businessHoursConfig.analytics?.performanceMetrics?.slaAccuracy || 0}%
                  </p>
                  <p className="text-sm text-gray-600">SLA Accuracy</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {businessHoursConfig.analytics?.performanceMetrics?.calculationSpeed || 0}s
                  </p>
                  <p className="text-sm text-gray-600">Calculation Speed</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">
                    {businessHoursConfig.analytics?.performanceMetrics?.errorRate || 0}%
                  </p>
                  <p className="text-sm text-gray-600">Error Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BusinessHoursSetup;
